import { useRef, useState, useLayoutEffect } from "react";

type OverlayPillStyle = {
  left: number;
  width: number;
};

type UseSegmentedControlParams = {
  value: string;
  stretchOnHover: boolean | undefined;
};

export function useSegmentedControl({
  value,
  stretchOnHover,
}: UseSegmentedControlParams) {
  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const overlayPillRef = useRef<HTMLDivElement>(null);
  const [overlayPillStyle, setOverlayPillStyle] = useState<OverlayPillStyle>({
    left: 0,
    width: 0,
  });

  function updateOverlayPill() {
    const selectedOption = optionRefs.current.get(value);
    const container = containerRef.current;
    const overlayPill = overlayPillRef.current;

    if (!selectedOption || !container || !overlayPill) return;

    const selectedOptionRect = selectedOption.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    overlayPill.style.transitionTimingFunction = "ease-in-out";
    overlayPill.style.transitionDuration = "0.20s";

    setOverlayPillStyle({
      left: selectedOptionRect.left - containerRect.left,
      width: selectedOptionRect.width,
    });
  }

  useLayoutEffect(() => {
    updateOverlayPill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function onOptionMouseOver(key: string) {
    if (!stretchOnHover) return;

    const hoveredOption = optionRefs.current.get(key);
    const selectedOption = optionRefs.current.get(value);
    const overlayPill = overlayPillRef.current;

    if (!hoveredOption || !selectedOption || !overlayPill) return;

    const hoveredOptionRect = hoveredOption.getBoundingClientRect();
    const selectedOptionRect = selectedOption.getBoundingClientRect();

    const isOnTheLeft = hoveredOptionRect.left < selectedOptionRect.left;

    overlayPill.style.transitionDuration = "0.5s";
    overlayPill.style.transitionTimingFunction = "ease-out";
    setOverlayPillStyle((prev) => ({
      left: isOnTheLeft
        ? prev.left - (selectedOptionRect.left - hoveredOptionRect.left) * 0.2
        : prev.left,
      width:
        prev.width +
        Math.abs(selectedOptionRect.left - hoveredOptionRect.left) * 0.2,
    }));
  }

  function onOptionMouseLeave() {
    if (!stretchOnHover) return;

    const overlayPill = overlayPillRef.current;

    if (!overlayPill) return;

    overlayPill.style.transitionDuration = "0.10s";
    overlayPill.style.transitionTimingFunction = "linear";

    updateOverlayPill();

    setTimeout(() => {
      overlayPill.style.transitionTimingFunction = "ease-in-out";
      overlayPill.style.transitionDuration = "0.25s";
    }, 250);
  }

  return {
    overlayPillStyle,
    optionRefs,
    containerRef,
    onOptionMouseOver,
    onOptionMouseLeave,
    overlayPillRef,
  };
}
